import { Offer } from "../features/offers/offferModels";

export const getOfferLabel = (offerDetail: Offer) => {

  if (offerDetail.offerType == "PERCENT") {
    return offerDetail.offerRate + " % off upto Rs." + offerDetail.maxDiscount;
  } else {
    return "Flat Rs." + offerDetail.maxDiscount + " Off";
  }
};

export const getOfferLabelUS = (offerDetail: Offer) => {

  if (offerDetail.offerType == "PERCENT") {
    return offerDetail.offerRate + " % off upto $." + offerDetail.maxDiscount;
  } else {
    return "Flat $." + offerDetail.maxDiscount + " Off";
  }
};

// ────────────────────────────────────────────────────────────────────
// Stacked-coupon category helpers.
// A cart can carry up to one coupon per category. The discriminator
// `offer_type` (raw list shape) or `offerType` (validated shape) maps to:
//   FLAT / PERCENT / FLATFEE → ORDER (order-level discount)
//   FREE_ITEM                → FREE_ITEM
//   FREE_DELIVERY            → FREE_DELIVERY
// ────────────────────────────────────────────────────────────────────
export type OfferCategory = 'ORDER' | 'FREE_ITEM' | 'FREE_DELIVERY' | 'BOGO';

export const getOfferCategory = (coupon: any): OfferCategory => {
  const t = String(coupon?.offer_type ?? coupon?.offerType ?? '').toUpperCase();
  if (t === 'BOGO') return 'BOGO';
  if (t === 'FREE_ITEM') return 'FREE_ITEM';
  if (t === 'FREE_DELIVERY') return 'FREE_DELIVERY';
  return 'ORDER';
};

export const getAppliedCouponByCategory = (
  appliedCoupons: any[] | undefined | null,
  category: OfferCategory
) => {
  if (!Array.isArray(appliedCoupons)) return null;
  return appliedCoupons.find((c) => getOfferCategory(c) === category) || null;
};

// Raw (discount-free) cart subtotal used for dismissal comparisons.
//
// Cart items' `subTotal` field reflects whatever the cart-watcher last wrote —
// for a BOGO get target it can be 0 while the offer is applied, then jump back
// to full price the instant the user removes the offer. Comparing against that
// moving target makes the `subTotal > stored` boundary flap (a Remove click
// would immediately release the dismissal and the offer would re-auto-apply).
//
// This helper computes `Σ qty × (base + modifiers)` per item, which stays
// stable across BOGO/ORDER discount toggles. All dismissal store/read sites
// must use this so the comparison is monotonic on real cart growth only.
export const computeRawCartSubtotal = (
  cartItems: any[] | undefined | null
): number => {
  if (!Array.isArray(cartItems)) return 0;
  return cartItems.reduce((sum: number, item: any) => {
    const qty = Number(item?.quantity || 0);
    if (qty <= 0) return sum;
    const base = Number(item?.price || 0);
    const modifiers = Array.isArray(item?.options)
      ? item.options.reduce((s: number, opt: any) => {
          const p = Number(opt?.price) || 0;
          const oq = Number(opt?.quantity) > 0 ? Number(opt?.quantity) : 1;
          return s + p * oq;
        }, 0)
      : 0;
    return sum + qty * (base + modifiers);
  }, 0);
};

// Per-offer auto-apply dismissal.
//
// When a user manually removes an offer, we suppress its auto-apply until the
// cart subtotal grows past the value it had at dismissal — but the suppression
// is scoped to the single offer id, not the whole category. Removing
// "free pepsi" should not block "free item 12" from auto-applying; they share
// the FREE_ITEM slot but are independent offers as far as the user is
// concerned.
//
// All readers (cart-watcher BOGO loop, mobileCart auto-apply, mobileCart BOGO
// popup) must compare against the same `computeRawCartSubtotal` so the
// `> stored` boundary doesn't flap from BOGO/ORDER discount toggles.
const OFFER_DISMISS_PREFIX = 'autoApplyDismissedOffer:';

export const dismissOfferAtSubtotal = (
  offerId: string | number | undefined | null,
  subTotal: number
): void => {
  if (offerId == null) return;
  try {
    sessionStorage.setItem(
      OFFER_DISMISS_PREFIX + String(offerId),
      String(Number(subTotal) || 0)
    );
  } catch {
    /* sessionStorage unavailable */
  }
};

// Auto-releases when the caller's `currentSubTotal` grows past the stored
// value (matches the "user can re-unlock by spending more" UX). The
// sessionStorage.removeItem side-effect is fine inside effect bodies — it
// doesn't subscribe React to the storage, so it can't cause a render cycle.
export const isOfferDismissed = (
  offerId: string | number | undefined | null,
  currentSubTotal: number
): boolean => {
  if (offerId == null) return false;
  try {
    const key = OFFER_DISMISS_PREFIX + String(offerId);
    const raw = sessionStorage.getItem(key);
    if (raw == null) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return true;
    if (Number(currentSubTotal) > at) {
      sessionStorage.removeItem(key);
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

export const clearOfferDismissal = (
  offerId: string | number | undefined | null
): void => {
  if (offerId == null) return;
  try {
    sessionStorage.removeItem(OFFER_DISMISS_PREFIX + String(offerId));
  } catch {
    /* sessionStorage unavailable */
  }
};

// Global auto-apply suppression.
//
// Distinct from the per-offer dismissal above: once the user removes an
// offer that was AUTO-applied, the app must stop auto-applying ANY offer —
// every coupon category (ORDER / FREE_ITEM / FREE_DELIVERY) AND BOGO — until
// the user manually applies one. Unlike per-offer dismissal it does NOT
// auto-release when the cart grows; only a manual apply (or reload/logout)
// clears it. Item-level offers (login-gated, reconciled separately) are NOT
// gated by this flag and keep applying.
//
// Stored in sessionStorage so it shares the per-offer dismissals' "reload =
// fresh app" lifecycle (CartWatcherService wipes it on mount and on logout).
const SUPPRESS_ALL_AUTO_APPLY_KEY = 'autoApplySuppressedAll';

export const suppressAllAutoApply = (): void => {
  try {
    sessionStorage.setItem(SUPPRESS_ALL_AUTO_APPLY_KEY, '1');
  } catch {
    /* sessionStorage unavailable */
  }
};

export const isAllAutoApplySuppressed = (): boolean => {
  try {
    return sessionStorage.getItem(SUPPRESS_ALL_AUTO_APPLY_KEY) === '1';
  } catch {
    return false;
  }
};

export const clearAllAutoApplySuppression = (): void => {
  try {
    sessionStorage.removeItem(SUPPRESS_ALL_AUTO_APPLY_KEY);
  } catch {
    /* sessionStorage unavailable */
  }
};

// Was this offer/coupon placed automatically (vs. the user manually applying
// it)? Auto-apply only ever happens for offers flagged `auto_apply === true`;
// coupon-category auto-applies additionally stamp `_autoApplied === true` on
// the applied entry, while auto-applied BOGO offers carry only the underlying
// `auto_apply` flag. Either signal means "the app applied it for the user".
export const wasAutoApplied = (offer: any): boolean =>
  offer?._autoApplied === true || offer?.auto_apply === true;

// Shared BOGO engine: computes how many units of each cart item this offer
// will claim as free/discounted, honoring buyQty:getQty ratio, the
// equal-or-lesser-value rule, and a cross-offer claim ledger so two BOGOs
// don't double-spend the same buy units.
//
// Returns `Map<itemId, claimedQty>`. Mutates `claimedBuyQty` as a side
// effect (each consumed buy unit is added to it so subsequent offers in the
// same pass see fewer available buys).
//
// Used by both the global cart-watcher (to write discounts to cart items)
// and the BOGO popup builder in mobileCart (to celebrate one popup per
// claimed item with the correct discount label).
export const computeBogoItemClaims = (
  offer: any,
  cartItems: any[] | undefined | null,
  claimedBuyQty: Map<string, number>
): Map<string, number> => {
  const itemClaims = new Map<string, number>();
  if (!Array.isArray(cartItems) || cartItems.length === 0) return itemClaims;
  const bogo = offer?.bogo || {};
  const buyItemIds: string[] = (Array.isArray(bogo?.buyItems) ? bogo.buyItems : [])
    .map((b: any) => b?.itemId)
    .filter(Boolean);
  const getItemIds: string[] = (Array.isArray(bogo?.getItems) ? bogo.getItems : [])
    .map((g: any) => g?.itemId)
    .filter(Boolean);
  if (buyItemIds.length === 0 || getItemIds.length === 0) return itemClaims;
  const buyQty = Number(bogo?.buyQty || 1);
  const getQty = Number(bogo?.getQty || 1);
  const offerId = offer?.id;

  const isSameItem =
    buyItemIds.length === 1 && getItemIds.includes(buyItemIds[0]);

  if (isSameItem) {
    const sameItemEntry = cartItems.find((it: any) =>
      buyItemIds.includes(it?.itemId)
    );
    if (sameItemEntry) {
      const total = Number(sameItemEntry.quantity || 0);
      const claimable = Math.min(Math.max(0, total - buyQty), getQty);
      if (claimable > 0) {
        itemClaims.set(sameItemEntry.itemId, claimable);
      }
    }
    return itemClaims;
  }

  // BUY units, descending by price; subtract qty already claimed by earlier
  // offers in this pass via the shared ledger.
  const buyUnits: number[] = [];
  cartItems.forEach((item: any) => {
    if (!buyItemIds.includes(item?.itemId)) return;
    const total = Number(item?.quantity || 0);
    if (total <= 0) return;
    const claimed = claimedBuyQty.get(item.itemId) || 0;
    const headroom = Math.max(0, total - claimed);
    const price = Number(item?.price || 0);
    for (let i = 0; i < headroom; i++) buyUnits.push(price);
  });
  buyUnits.sort((a, b) => b - a);

  const safeBuyQty = Math.max(1, buyQty);
  const safeGetQty = Math.max(1, getQty);

  // DISTINCT get items present in cart, highest price first. Per spec, each
  // qualifying buy GROUP (buyQty buy units) frees ONE distinct get item —
  // getQty units of it — rather than stacking several free units onto a
  // single item while other get items stay undiscounted. So:
  //   3 buys + 2 distinct gets → both gets free (the 3rd buy has no get to map)
  //   2 buys + 2 gets          → 2:2
  //   1 buy  + 2 gets          → only the priciest get is free
  // Exclude entries already discount-applied by ANOTHER offer this pass.
  type GetEntry = { itemId: string; price: number; qty: number };
  const getEntries: GetEntry[] = [];
  cartItems.forEach((item: any) => {
    if (!getItemIds.includes(item?.itemId)) return;
    const total = Number(item?.quantity || 0);
    if (total <= 0) return;
    const claimedByOther =
      item?.is_discount_applied === true &&
      item?.offer_data &&
      item?.offer_data?.offerId !== offerId;
    if (claimedByOther) return;
    getEntries.push({
      itemId: item.itemId,
      price: Number(item?.price || 0),
      qty: total,
    });
  });
  getEntries.sort((a, b) => b.price - a.price);

  // Assign one distinct get item per available buy group, richest get first.
  let groupsRemaining = Math.floor(buyUnits.length / safeBuyQty);
  // Anchor for the equal-or-lesser gate walks down the sorted buy groups as
  // each is consumed (buyUnits is descending, so the next group's qualifying
  // buy price is buyUnits[anchorCursor]).
  let anchorCursor = 0;
  for (const ge of getEntries) {
    if (groupsRemaining <= 0) break;
    if (bogo?.equalOrLesserValue === true) {
      const anchorBuyPrice = buyUnits[anchorCursor];
      if (Number.isFinite(anchorBuyPrice) && ge.price > anchorBuyPrice) {
        // Too expensive for even the best remaining buy unit — skip this get
        // item without consuming a buy group; a cheaper get may still qualify.
        continue;
      }
    }
    const claim = Math.min(safeGetQty, ge.qty);
    if (claim <= 0) continue;
    itemClaims.set(ge.itemId, claim);
    groupsRemaining -= 1;
    anchorCursor += safeBuyQty;
  }

  // Reserve buy-side qty in the shared ledger so a later BOGO in this same
  // pass can't double-count. One buy group was consumed per distinct get item
  // freed, so reserve `(# distinct gets freed) × buyQty` buy units.
  if (itemClaims.size > 0) {
    let toReserve = itemClaims.size * safeBuyQty;
    cartItems.forEach((item: any) => {
      if (toReserve <= 0) return;
      if (!buyItemIds.includes(item?.itemId)) return;
      const total = Number(item?.quantity || 0);
      if (total <= 0) return;
      const already = claimedBuyQty.get(item.itemId) || 0;
      const headroom = Math.max(0, total - already);
      const take = Math.min(headroom, toReserve);
      if (take > 0) {
        claimedBuyQty.set(item.itemId, already + take);
        toReserve -= take;
      }
    });
  }

  return itemClaims;
};

// Equal-or-lesser-value gate for BOGO offers.
//
// When the offer payload has `bogo.equalOrLesserValue === true`, the get item
// being discounted must cost at most as much as the most expensive buy item
// in the cart. Implements the conventional BOGO rule "free item must be equal
// or lesser value than the qualifying item". Same-item BOGOs trivially pass
// (same price both sides).
export const bogoEqualOrLesserSatisfied = (
  bogo: any,
  cartItems: any[] | undefined | null,
  buyItemIds: string[],
  getItemIds: string[]
): boolean => {
  if (!bogo || bogo.equalOrLesserValue !== true) return true;
  if (!Array.isArray(cartItems)) return false;
  let maxBuyPrice = 0;
  cartItems.forEach((item: any) => {
    if (Number(item?.quantity || 0) <= 0) return;
    if (buyItemIds.includes(item?.itemId)) {
      const p = Number(item?.price || 0);
      if (p > maxBuyPrice) maxBuyPrice = p;
    }
  });
  return cartItems.some(
    (item: any) =>
      Number(item?.quantity || 0) > 0 &&
      getItemIds.includes(item?.itemId) &&
      Number(item?.price || 0) <= maxBuyPrice
  );
};

export const getOrderLevelAppliedCoupon = (appliedCoupons: any[] | undefined | null) =>
  getAppliedCouponByCategory(appliedCoupons, 'ORDER');

export const getAppliedCouponIds = (
  appliedCoupons: any[] | undefined | null
): (string | number)[] => {
  console.log({appliedCoupons});
  
  if (!Array.isArray(appliedCoupons)) return [];
  return appliedCoupons.map((c) => c?.id).filter((id) => id != null);
};

// Item-level offer ids stamped on cart entries — a menu offer applied to a
// single item (RECONCILE_ITEM_OFFER / add-to-cart sets the top-level
// `item.offerId`, e.g. "4.65% Off" on meen kulambu). These are distinct from
// coupon offers (`appliedCoupons`) and BOGO (`offer_data.offerId`), so they
// must be merged into the totals/place-order `offerIds` for the server to
// account for them. De-duplicated.
export const getItemLevelOfferIds = (
  cartItems: any[] | undefined | null
): (string | number)[] => {
  if (!Array.isArray(cartItems)) return [];
  const ids = cartItems
    .filter((i: any) => i?.offerId && Number(i?.quantity || 0) > 0)
    .map((i: any) => i.offerId);
  return Array.from(new Set(ids));
};

// Filter applied coupons down to those eligible for the given order type.
// Offers with a null/undefined/empty `orderTypeIds` array are valid for all
// order types and are always kept. Offers with a non-empty `orderTypeIds` are
// kept only when `orderTypeId` appears in the array (compared as strings to
// handle mixed number/string IDs from the API).
export const filterOffersByOrderType = (
  appliedCoupons: any[] | undefined | null,
  orderTypeId: any
): any[] => {
  if (!Array.isArray(appliedCoupons)) return [];
  if (orderTypeId == null) return appliedCoupons;
  return appliedCoupons.filter((c: any) => {
    const ids: any[] = c?.orderTypeIds;
    if (!Array.isArray(ids) || ids.length === 0) return true;
    const target = String(orderTypeId);
    return ids.some((id: any) => String(id) === target);
  });
};

// Build the `OfferCaptureRequests` array the totals/place-order APIs expect —
// one `{ offerId, promoCode, items? }` entry per applied offer. Applied coupons
// carry their promo/offer code; item-level offers (stamped on cart entries)
// carry the code via `itemLevelPromoCodes` (below), or none when auto-applied
// from the menu. Entries are merged — not de-duplicated — by offerId.
//
// `menuDetails` (optional): the current restaurant menu. When provided,
// item-level offer IDs are validated against it — if the menu no longer
// returns an `offerId` for that item (because the offer is not available for
// the current order type), the ID is skipped. This prevents stale stamped
// `offerId` values from being sent after an order-type switch.
//
// `itemLevelPromoCodes` (optional): `state.cart.itemLevelPromoCodes` — the
// promo code the user actually typed, keyed by offerId. Item-level offers are
// stamped onto cart entries by the cart-watcher and carry no code of their
// own, so without this map a promo-code-applied item offer would go out with
// `promoCode: ""` and the backend would not attribute the redemption to the
// code. Coupon-level codes still win when both are present.
export const buildOfferCaptureRequests = (
  appliedCoupons: any[] | undefined | null,
  cartItems: any[] | undefined | null,
  menuDetails?: any[] | null,
  itemLevelPromoCodes?: Record<string, string> | null
): Array<{ offerId: string; promoCode: string; items?: Array<{ itemId: string; isDiscountApplied: boolean }> }> => {
  type Entry = { offerId: string; promoCode: string; items?: Array<{ itemId: string; isDiscountApplied: boolean }> };
  // One entry per offerId, merged across both sources. An offer can appear as
  // BOTH an applied coupon (carrying the promo code) and as stamped cart items
  // (carrying the item list) — dropping the second occurrence would lose one
  // half of the entry, so merge instead of de-duplicating away.
  const order: string[] = [];
  const byId = new Map<string, Entry>();
  const push = (offerId: any, promoCode: any, items?: Array<{ itemId: string; isDiscountApplied: boolean }>) => {
    if (offerId == null || offerId === '') return;
    const id = String(offerId);
    let entry = byId.get(id);
    if (!entry) {
      entry = { offerId: id, promoCode: '' };
      byId.set(id, entry);
      order.push(id);
    }
    // First non-empty code wins — never let a later blank overwrite it.
    const code = String(promoCode ?? '');
    if (code && !entry.promoCode) entry.promoCode = code;
    if (items && items.length > 0) {
      if (!entry.items) entry.items = [];
      items.forEach((it) => {
        if (!entry!.items!.some((e) => e.itemId === it.itemId)) entry!.items!.push(it);
      });
    }
  };
  if (Array.isArray(appliedCoupons)) {
    appliedCoupons.forEach((c: any) => {
      const offerType = String(c?.offer_type ?? c?.offerType ?? '').toUpperCase();
      let freeItems: Array<{ itemId: string; isDiscountApplied: boolean }> | undefined;
      if (offerType === 'FREE_ITEM' && c?._chosenFreeItemId) {
        freeItems = [{ itemId: String(c._chosenFreeItemId), isDiscountApplied: true }];
      }
      push(c?.id, c?.promoCode ?? c?.offerCode ?? (Array.isArray(c?.promoCodes) ? c?.promoCodes[0] : c?.promoCodes) ?? '', freeItems);
    });
  }
  // Item-level offer IDs: only include if the menu currently exposes an offer
  // for that cart item. When the order type changes (e.g. pickup → delivery),
  // the menu API stops returning offerId/offerPrice for items whose offers are
  // not available for that type — those should not be sent to the totals API.
  // FREE_ITEM coupons never block item-level offers (both are item-scoped) —
  // consistent with the cart-watcher which also lets them coexist freely.
  // Group cart items by offerId so all items belonging to the same offer
  // are sent together in one offerCaptureRequests entry with an items array.
  if (Array.isArray(cartItems)) {
    const itemsByOfferId = new Map<string, Array<{ itemId: string; isDiscountApplied: boolean }>>();
    cartItems
      .filter((i: any) => i?.offerId && Number(i?.quantity || 0) > 0)
      .forEach((cartItem: any) => {
        if (Array.isArray(menuDetails) && menuDetails.length > 0) {
          const menuItem = menuDetails.find((m: any) => m?.itemId === cartItem?.itemId);
          if (!menuItem?.offerId) return;
          // Only skip an exclusive item-level offer when a non-FREE_ITEM exclusive
          // coupon is active — FREE_ITEM and item-level offers are both item-scoped
          // and coexist freely (consistent with cart-watcher logic).
          const hasNonFreeItemExclusiveCoupon = Array.isArray(appliedCoupons) &&
            appliedCoupons.some((c: any) =>
              String(c?.offer_type ?? c?.offerType ?? '').toUpperCase() !== 'FREE_ITEM' &&
              (c?.combineOffer === true || c?.combine_offer === true)
            );
          if (hasNonFreeItemExclusiveCoupon && menuItem?.combineOffer === true) return;
        }
        const id = String(cartItem.offerId);
        if (!itemsByOfferId.has(id)) itemsByOfferId.set(id, []);
        itemsByOfferId.get(id)!.push({ itemId: String(cartItem.itemId), isDiscountApplied: true });
      });
    itemsByOfferId.forEach((items, offerId) => {
      push(offerId, itemLevelPromoCodes?.[offerId] ?? '', items);
    });
  }
  return order.map((id) => byId.get(id) as Entry);
};

export const isCouponApplied = (
  appliedCoupons: any[] | undefined | null,
  couponId: any
): boolean => {
  if (!Array.isArray(appliedCoupons) || couponId == null) return false;
  return appliedCoupons.some((c) => c?.id === couponId);
};

// FREE_ITEM coupons' chosen items are not real cart entries — they live only
// on the coupon's `_chosenFreeItemId`. This materializes each as a $0 order
// line so the totals/place-order APIs receive the free item inside
// `orderItems` (instead of a separate `freeItemSelections` array). The line is
// always built from `_chosenFreeItemId`, falling back to coupon metadata when
// the item isn't resolvable in `restaurantMenuDetails` (e.g. a promo item not
// in the regular menu) — so the free item is never silently dropped.
export const buildFreeItemOrderLines = (
  appliedCoupons: any[] | undefined | null,
  restaurantMenuDetails?: any[] | null
): any[] => {
  if (!Array.isArray(appliedCoupons)) return [];
  return appliedCoupons
    .filter((c: any) => {
      const t = String(c?.offer_type ?? c?.offerType ?? '').toUpperCase();
      return t === 'FREE_ITEM' && c?._chosenFreeItemId;
    })
    .map((c: any) => {
      const menuItem = Array.isArray(restaurantMenuDetails)
        ? restaurantMenuDetails.find((m: any) => m?.itemId === c._chosenFreeItemId)
        : null;
      // `noOfFreeItems` is the authoritative count of free units granted.
      // Falls back to `max_cap`/`maxCap` for older offer shapes, then 1.
      const noOfFree = Number(c?.noOfFreeItems);
      const cap = Number(c?.max_cap ?? c?.maxCap);
      const freeQty = (Number.isFinite(noOfFree) && noOfFree > 0)
        ? Math.floor(noOfFree)
        : (Number.isFinite(cap) && cap > 0 ? Math.floor(cap) : 1);
      return {
        itemId: c._chosenFreeItemId,
        offerId: null,
        itemName: menuItem?.itemName || c?.name || '',
        itemAltName: menuItem?.itemAltName || '',
        price: '0.00',
        originalPrice: menuItem?.price || '0',
        quantity: String(freeQty),
        subTotal: '0.00',
        comment: '',
        is_discount_applied: true,
        options: [],
        tax: menuItem?.tax || [],
        offer_data: { offerId: c.id, isFreeItem: true, freeItemCouponId: c.id },
        offer_amount: 0,
        availability: menuItem?.availability || [],
        stockQuantity: menuItem?.stockQuantity ?? null,
        alertQuantity: menuItem?.alertQuantity ?? null,
      };
    });
};

// Exclusive coupons: the v2-menu camelCase `combineOffer`, the
// customer-coupon snake_case `combine_offer`, and the offers-API
// `isExclusive` (0/1) all indicate the same thing — when the flag is
// set, no other coupon may coexist with it.
export const isExclusiveCoupon = (coupon: any): boolean =>
  coupon?.combineOffer === true ||
  coupon?.combine_offer === true ||
  Number(coupon?.isExclusive) === 1;

export const hasExclusiveApplied = (
  appliedCoupons: any[] | undefined | null
): boolean => {
  if (!Array.isArray(appliedCoupons)) return false;
  return appliedCoupons.some(isExclusiveCoupon);
};

// Item-level exclusivity: a cart item carries an inline offer (offerId +
// offerPrice + offerType) sourced from the v2 menu. When that offer has
// `combineOffer === true`, no coupons (order / free-item / free-delivery)
// may apply on top of it.
export const cartHasExclusiveItemOffer = (
  cartItems: any[] | undefined | null,
  restaurantMenuDetails: any[] | undefined | null
): boolean => {
  if (!Array.isArray(cartItems) || cartItems.length === 0) return false;
  if (!Array.isArray(restaurantMenuDetails)) return false;
  return cartItems.some((cartItem: any) => {
    if (Number(cartItem?.quantity) <= 0) return false;
    // The exclusive lock only applies while the offer is actively stamped on
    // the cart item. reconcileItemOffer({apply:false}) sets offerId to null
    // (e.g. after a totals API error), so the lock is released immediately
    // and the user can apply other offers without being blocked.
    if (!cartItem?.offerId) return false;
    const menuItem = restaurantMenuDetails.find(
      (m: any) => m?.itemId === cartItem?.itemId
    );
    if (!menuItem) return false;
    const hasItemOffer =
      menuItem.offerId != null || menuItem.offerPrice != null;
    return hasItemOffer && (menuItem.combineOffer === true || menuItem.combine_offer === true);
  });
};

// Returns true when a menu item is orderable — enabled, in stock, and not
// temporarily unavailable. Used to gate FREE_ITEM offer eligibility so that
// out-of-stock / disabled items are never auto-added as free items.
export const isMenuItemOrderable = (m: any): boolean => {
  if (!m) return false;
  if (m.enable === false) return false;
  if (m.isCategoryOff === true) return false;
  if (m.stockQuantity !== null && m.stockQuantity !== undefined && Number(m.stockQuantity) < 1) return false;
  if (m.itemUnAvailableUntil) {
    const unavailDate = new Date(m.itemUnAvailableUntil);
    if (unavailDate.getFullYear() > 3000) return false;
    if (unavailDate > new Date()) return false;
  }
  return true;
};
